import React, { useCallback, useRef, useEffect } from 'react';
import {
  View,
  ActivityIndicator,
  RefreshControl,
  TouchableOpacity,
} from 'react-native';
import styled from '@emotion/native';
import { Href, useRouter } from 'expo-router';
import { FlashList, ListRenderItem, ViewToken } from '@shopify/flash-list';
import { BodyText, H3, H4 } from '@/shared/components/StyledText';
import { FullScreenLoader } from '@/shared/components/FullScreenLoader';
import Spacer from '@/shared/components/Spacer';
import Button from '@/shared/components/Button';
import { sand, green } from '@/shared/styles/colors';
import { useNotificationList } from './useNotificationList';
import NotificationItem from './NotificationItem';
import type { Notification } from '@/shared/types/generated';
import { processDeepLinkUrl } from './helpers';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import useStore from '@/stores/useStore';
import SentryService from '@/services/sentry/Sentry.service';
import { ROUTES } from '@/shared/constants/routes';

interface NotificationListProps {
  onNotificationPress?: (notification: Notification) => void;
  onError?: (error: Error) => void;
  showScrollbar?: boolean;
}

const NotificationList: React.FC<NotificationListProps> = ({
  onNotificationPress,
  onError,
  showScrollbar = false,
}) => {
  const router = useRouter();
  const isDesktop = useIsDesktop();
  const isStylist = useStore((state) => state.isStylist);

  // Batch update functionality for marking notifications as read
  const batchUpdateQueue = useRef<Set<Notification>>(new Set());
  const batchUpdateTimeout = useRef<NodeJS.Timeout | null>(null);
  const markedAsReadRef = useRef<Set<string>>(new Set());

  const {
    notifications,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    refresh,
    markAsRead,
  } = useNotificationList({
    pageSize: 20,
    onError,
  });

  // Batch process notifications to mark as read
  const processBatchUpdate = useCallback(async () => {
    if (batchUpdateQueue.current.size === 0) return;

    const notificationsToUpdate = Array.from(batchUpdateQueue.current);
    batchUpdateQueue.current.clear();

    // Process notifications in parallel batches of 5 to avoid overwhelming the API
    const batchSize = 5;
    const batches = [];
    for (let i = 0; i < notificationsToUpdate.length; i += batchSize) {
      batches.push(notificationsToUpdate.slice(i, i + batchSize));
    }

    try {
      // Process each batch in parallel
      for (const batch of batches) {
        await Promise.allSettled(
          batch.map(async (notification) => {
            try {
              await markAsRead(notification.id);
            } catch (error) {
              // Remove from marked set if failed, so we can retry later
              markedAsReadRef.current.delete(notification.id.toString());
              SentryService.captureError(
                `Failed to mark notification ${notification.id} as read:`,
                error,
              );
            }
          }),
        );
      }
    } catch (error) {
      SentryService.captureError('Notification batch update error:', error);
    }
  }, [markAsRead]);

  // Add notification to batch update queue
  const queueNotificationForBatchUpdate = useCallback(
    (notification: Notification) => {
      const notificationId = notification.id.toString();

      // Skip if already marked or queued
      if (notification.isRead || markedAsReadRef.current.has(notificationId)) {
        return;
      }

      // Add to tracking and queue
      markedAsReadRef.current.add(notificationId);
      batchUpdateQueue.current.add(notification);

      // Debounce batch processing - wait 1 second after last addition
      if (batchUpdateTimeout.current) {
        clearTimeout(batchUpdateTimeout.current);
      }

      batchUpdateTimeout.current = setTimeout(() => {
        processBatchUpdate();
        batchUpdateTimeout.current = null;
      }, 1000);
    },
    [processBatchUpdate],
  );

  // Cleanup batch timeout on unmount
  useEffect(() => {
    return () => {
      if (batchUpdateTimeout.current) {
        clearTimeout(batchUpdateTimeout.current);
        // Process any remaining items before unmounting
        processBatchUpdate();
      }
    };
  }, [processBatchUpdate]);

  // Handle visibility changes for automatic read status
  const handleViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      viewableItems.forEach((viewableItem) => {
        const notification = viewableItem.item as Notification;
        // Add to batch queue instead of immediate individual update
        queueNotificationForBatchUpdate(notification);
      });
    },
    [queueNotificationForBatchUpdate],
  );

  // Viewability configuration for FlashList
  const viewabilityConfig = useRef({
    itemVisiblePercentThreshold: 60, // Item must be 60% visible
    minimumViewTime: 500, // Must be visible for at least 500ms before callback
  }).current;

  // Handle notification press and navigation
  const handleNotificationPress = useCallback(
    (notification: Notification) => {
      onNotificationPress?.(notification);

      // Navigate to deep link if available
      if (notification.deepLinkUrl) {
        try {
          // Process the URL to extract only path and query params to prevent page refresh
          const processedUrl = processDeepLinkUrl(notification.deepLinkUrl);
          router.push(processedUrl as Href);
        } catch (error) {
          SentryService.captureError('Failed to navigate to deep link:', error);
        }
      }
    },
    [onNotificationPress, router],
  );

  // Handle settings navigation
  const handleSettingsPress = useCallback(() => {
    router.push(ROUTES.SETTINGS.path);
  }, [router]);

  // Handle end reached for infinite loading
  const handleEndReached = useCallback(() => {
    if (hasMore && !loading && !loadingMore) {
      loadMore();
    }
  }, [hasMore, loading, loadingMore, loadMore]);

  // Handle pull to refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Render each notification item
  const renderNotificationItem: ListRenderItem<Notification> = useCallback(
    ({ item: notification, index }) => (
      <NotificationItem
        notification={notification}
        onPress={() => handleNotificationPress(notification)}
        isAlternate={index % 2 !== 0}
      />
    ),
    [handleNotificationPress],
  );

  // Render loading footer
  const renderListFooter = useCallback(() => {
    if (loadingMore) {
      return (
        <LoadMoreContainer>
          <ActivityIndicator size="small" color={green} />
        </LoadMoreContainer>
      );
    }

    return <Spacer height={40} />;
  }, [loadingMore]);

  // Handle explore stylists navigation for clients
  const handleExploreStylists = useCallback(() => {
    router.push(ROUTES.EXPLORE.path);
  }, [router]);

  // Render empty state
  const renderEmptyState = useCallback(() => {
    if (loading) return null;

    if (isStylist) {
      return (
        <EmptyStateContainer isDesktop={isDesktop}>
          <EmptyStateContent isDesktop={isDesktop}>
            <EmptyTitle>STAY TUNED</EmptyTitle>
            <EmptyDescription>
              <EmptyDescriptionText isStylist={true}>
                {`Important updates about your\nsessions will show up here. `}
              </EmptyDescriptionText>
              <TouchableOpacity onPress={handleSettingsPress}>
                <EmptySettingsLink isStylist={true}>
                  Edit preferences in your settings
                </EmptySettingsLink>
              </TouchableOpacity>
            </EmptyDescription>
          </EmptyStateContent>
        </EmptyStateContainer>
      );
    } else {
      return (
        <EmptyStateContainer isDesktop={isDesktop}>
          <EmptyStateContent isDesktop={isDesktop}>
            <EmptyTitle>STAY TUNED</EmptyTitle>
            <EmptyDescription>
              <EmptyDescriptionText isStylist={false}>
                {`Important updates about your\nsessions will show up here.`}
              </EmptyDescriptionText>
              <TouchableOpacity onPress={handleSettingsPress}>
                <EmptySettingsLink isStylist={false}>
                  Edit preferences in your settings
                </EmptySettingsLink>
              </TouchableOpacity>
            </EmptyDescription>
            <ButtonWrapper>
              <Button
                text="Explore Stylists"
                onPress={handleExploreStylists}
                size="large"
              />
            </ButtonWrapper>
          </EmptyStateContent>
        </EmptyStateContainer>
      );
    }
  }, [
    loading,
    isDesktop,
    isStylist,
    handleSettingsPress,
    handleExploreStylists,
  ]);

  // Show initial loading state
  if (loading && notifications.length === 0) {
    return <FullScreenLoader />;
  }

  return (
    <Container>
      {notifications.length > 0 && (
        <Header isDesktop={isDesktop}>
          <HeaderTitle isDesktop={isDesktop}>STAY UPDATED</HeaderTitle>
          <SubtitleContainer isDesktop={isDesktop}>
            <HeaderSubtitle>Edit preferences in your </HeaderSubtitle>
            <TouchableOpacity onPress={handleSettingsPress}>
              <SettingsLink>settings</SettingsLink>
            </TouchableOpacity>
            <HeaderSubtitle>.</HeaderSubtitle>
          </SubtitleContainer>
        </Header>
      )}

      {notifications.length === 0 && !loading ? (
        renderEmptyState()
      ) : (
        <FlashList
          data={notifications}
          renderItem={renderNotificationItem}
          estimatedItemSize={120}
          showsVerticalScrollIndicator={showScrollbar}
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.5}
          refreshControl={
            <RefreshControl
              refreshing={loading}
              onRefresh={handleRefresh}
              tintColor={green}
              colors={[green]}
            />
          }
          ListFooterComponent={renderListFooter}
          contentContainerStyle={flashListContentStyle}
          keyExtractor={(item) => `notification-${item.id}`}
          onViewableItemsChanged={handleViewableItemsChanged}
          viewabilityConfig={viewabilityConfig}
        />
      )}
    </Container>
  );
};

// Styled components
const Container = styled(View)({
  flex: 1,
  backgroundColor: sand,
});

const Header = styled(View)<{ isDesktop: boolean }>(({ isDesktop }) => ({
  paddingHorizontal: 24,
  paddingTop: isDesktop ? 20 : 32,
  paddingBottom: isDesktop ? 16 : 24,
  backgroundColor: sand,
  alignItems: isDesktop ? 'flex-start' : 'center',
}));

const HeaderTitle = styled(H4)<{ isDesktop: boolean }>(({ isDesktop }) => ({
  color: green,
  fontSize: isDesktop ? 20 : 30,
  fontFamily: 'Lora',
  fontWeight: '400',
  letterSpacing: isDesktop ? -0.2 : -0.3,
  lineHeight: 33,
  textAlign: isDesktop ? 'left' : 'center',
  marginBottom: 8,
}));

const SubtitleContainer = styled(View)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: isDesktop ? 'flex-start' : 'center',
    flexWrap: 'wrap',
  }),
);

const HeaderSubtitle = styled(BodyText)({
  color: green,
  fontSize: 16,
  fontFamily: 'Helvetica',
  letterSpacing: -0.16,
});

const SettingsLink = styled(BodyText)({
  color: green,
  fontSize: 16,
  fontFamily: 'Helvetica',
  fontWeight: '700',
  letterSpacing: -0.16,
  textDecorationLine: 'underline',
});

const LoadMoreContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  paddingVertical: 20,
  paddingHorizontal: 16,
});

const EmptyStateContainer = styled(View)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: isDesktop ? 20 : 32,
  }),
);

const EmptyStateContent = styled(View)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    alignItems: 'center',
    maxWidth: isDesktop ? 379 : 308,
    zIndex: 1,
  }),
);

const EmptyTitle = styled(H3)({
  color: green,
  fontFamily: 'Lora',
  fontSize: 34,
  fontWeight: '400',
  lineHeight: 38,
  textAlign: 'center',
  marginBottom: 16,
});

const EmptyDescription = styled(View)({
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
});

const EmptyDescriptionText = styled(BodyText)<{ isStylist: boolean }>(
  ({ isStylist }) => ({
    color: green,
    fontSize: 16,
    fontFamily: isStylist ? 'Helvetica' : 'Inter',
    letterSpacing: -0.16,
    textAlign: 'center',
    lineHeight: 22,
  }),
);

const EmptySettingsLink = styled(BodyText)<{ isStylist: boolean }>(
  ({ isStylist }) => ({
    color: green,
    fontSize: 16,
    fontFamily: isStylist ? 'Helvetica' : 'Inter',
    fontWeight: isStylist ? '700' : '600', // Different weights
    letterSpacing: -0.16,
    textAlign: 'center',
    lineHeight: 22,
  }),
);

const ButtonWrapper = styled(View)({
  marginTop: 32,
  width: 330,
  maxWidth: '100%', // Prevents overflow on smaller screens
});

// FlashList content container style
const flashListContentStyle = {
  paddingBottom: 40,
};

export default NotificationList;
